import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOptimizedPublisherAnalysis } from './context/OptimizedPublisherAnalysisContext';
import './style/dataTable.scss';
import PlatformIcon from '../Common/PlatformIcon';
import { usePlatforms } from '../../contexts/PlatformsContext';
import * as XLSX from 'xlsx';
import { useUserPlans } from '../../hooks/useReactQueryHooks';
import AppsTrackingPlanModal from '../Common/Modal/AppsTrackingPlanModal';
import ExportUpgradeModal from '../Common/Modal/ExportUpgradeModal';
import { generateSlug } from '../../utils/seoUtils';
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics';

const DataTable = () => {
  const { getActivePlatforms, getPlatformDisplayName } = usePlatforms();
  const { tableData, loader, gameCards, showExportModal, setShowExportModal, publisher } = useOptimizedPublisherAnalysis();
  const { trackEvent } = useAnalytics();

  const prepareExportRows = () => {
    if (!tableData || !Array.isArray(tableData)) return [];
    return tableData.map(row => ({
      Name: row.name || '',
      Platform: (row.platform || (() => {
        const activePlatforms = getActivePlatforms();
        return activePlatforms.length > 0 ? activePlatforms[0].id : 'meta';
      })()),
      Category: row.category || '',
      Genre: row.genre ? (Array.isArray(row.genre) ? row.genre.join(', ') : row.genre) : '',
      Rating: row.rating?.average ? Number(row.rating.average.toFixed(1)) : '',
      "App Score": (row.app_score ?? row.appScore ?? row.score ?? row.keyquest_score ?? (row.stats && row.stats.app_score) ) || '',
      Downloads: row.downloads || '',
      Revenue: row.revenue || '',
      Released: row.created_at ? new Date(row.created_at).toLocaleDateString() : ''
    }));
  };

  const resolveAppScore = (r) => {
    if (!r) return '';
    const s = r.app_score ?? r.appScore ?? r.score ?? r.keyquest_score ?? r.stats?.app_score ?? r.game_details?.app_score;
    if (s == null || s === '') return '';
    const num = typeof s === 'number' ? s : parseFloat(String(s));
    return Number.isFinite(num) ? Number(num.toFixed(1)) : '';
  };

  const { data: plansData } = useUserPlans();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showExportInfoModal, setShowExportInfoModal] = useState(false);

  const determineCanExport = (plans) => {
    if (!plans) return false;
    const payload = plans.data ?? plans;
    if (Array.isArray(payload)) {
      if (payload.length === 0) return false;
      const activePlan = payload.find(p => p.is_active || p.is_current || p.active) || payload[0];
      const name = (activePlan && (activePlan.name || activePlan.title || activePlan.product?.name)) || '';
      const lname = String(name).toLowerCase();
      if (!name) return false;
      if (lname.includes('explorer') || lname.includes('free') || lname.includes('no plan') || lname.includes('trial')) return false;
      return true;
    }
    const objName = payload.product?.name || payload.name || payload.plan || payload.current_plan?.name || '';
    if (!objName) return false;
    const on = String(objName).toLowerCase();
    if (on.includes('explorer') || on.includes('free') || on.includes('no plan') || on.includes('trial')) return false;
    return true;
  }

  const canExport = determineCanExport(plansData);

  const downloadCSV = (rows, filename = 'publisher_apps.csv') => {
    try {
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
    }
  };

  const downloadXLSX = (rows, filename = 'publisher_apps.xlsx') => {
    try {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('XLSX export failed', err);
    }
  };

  const downloadPDF = (rows, filename = 'publisher_apps.pdf') => {
    // Simple printable HTML fallback - opens print dialog where user can save as PDF
    try {
      const popup = window.open('', '_blank');
      if (!popup) return;
      const style = `
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f4f4f4; }
      `;
      const headers = Object.keys(rows[0] || {});
      const htmlRows = rows.map(r => `
        <tr>${headers.map(h => `<td>${(r[h] !== undefined && r[h] !== null) ? r[h] : ''}</td>`).join('')}</tr>
      `).join('');
      const html = `
        <html>
          <head>
            <title>${filename}</title>
            <style>${style}</style>
          </head>
          <body>
            <h3>Export</h3>
            <table>
              <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
              <tbody>${htmlRows}</tbody>
            </table>
          </body>
        </html>
      `;
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      // Give the popup a moment to render then trigger print
      setTimeout(() => {
        try {
          popup.focus();
          popup.print();
          // Optionally close after print
          // popup.close();
        } catch (e) {
          console.error('PDF print failed', e);
        }
      }, 500);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  const handleExport = (type) => {
    const rows = prepareExportRows();
    if (!rows || rows.length === 0) return;
    if (!canExport) {
      // Open small info modal first if user is not allowed
      setShowExportModal(false);
      setShowExportInfoModal(true);
      return;
    }
    trackEvent(ANALYTICS_EVENTS.FEATURE.PUBLISHER_DASHBOARD_EXPORTED, {
      format: type,
      publisher_name: publisher?.name,
      row_count: tableData.length,
    });
    if (type === 'csv') return downloadCSV(rows);
    if (type === 'xlsx') return downloadXLSX(rows);
    if (type === 'pdf') return downloadPDF(rows);
  };
  
  if (loader) {
    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Game</th>
              <th>Category</th>
              <th>Genre</th>
              <th>Platform</th>
              <th>Ratings</th>
              <th>Downloads</th>
              <th>Revenue</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((row) => (
              <tr key={row}>
                <td>{row}</td>
                <td>
                  <div className="game-info">
                    <div className="placeholder-glow">
                      <span className="placeholder col-6"></span>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
                <td>
                  <div className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (!tableData || tableData.length === 0) {
    return (
      <div className="data-table-container">
        <div className="no-data">
          <p>No data available</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>Sno</th>
            <th>Game</th>
            <th>Category</th>
            <th>Genre</th>
            <th>Platform</th>
            <th>Ratings</th>
            <th>App Score</th>
            <th>Downloads</th>
            <th>Revenue</th>
            <th>Released On</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={row.item_id || index}>
              <td>{index + 1}</td>
              <td>
                <Link
                  to={`/xr/app/${generateSlug(row.name || '')}?id=${row.item_id}`}
                  state={{ source_surface: "publisher_dashboard" }}
                  onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.PUBLISHER_DASHBOARD_APP_SELECTED, { app_id: row.item_id, app_name: row.name })}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="game-info">
                    {row.image_url ? (
                      <img src={row.image_url} alt={row.name || 'Game'} />
                    ) : (
                      <div className="placeholder-image">
                        <i className="fas fa-gamepad"></i>
                      </div>
                    )}
                    <span>{row.name || 'Unknown Game'}</span>
                  </div>
                </Link>
              </td>
              <td>{row.category || 'N/A'}</td>
              <td>{row.genre ? row.genre.join(', ') : 'N/A'}</td>
                  <td>
                    {(() => {
                      const platform = row.platform || (() => {
                        const activePlatforms = getActivePlatforms();
                        return activePlatforms.length > 0 ? activePlatforms[0].id : 'meta';
                      })();

                      const buildPlatformUrl = () => {
                        // prefer explicit platform_url when present
                        if (row.platform_url) return row.platform_url;
                        
                        // Get platform data from API to build dynamic URLs
                        const activePlatforms = getActivePlatforms();
                        const currentPlatform = activePlatforms.find(p => p.id === platform);
                        
                        if (currentPlatform && currentPlatform.url_template) {
                          // Use platform-specific URL template from API
                          const template = currentPlatform.url_template;
                          const replacements = {
                            '{name}': encodeURIComponent(row.name || ''),
                            '{id}': row.item_id || row.id || '',
                            '{slug}': row.slug || row.meta_slug || row.experience_slug || '',
                            '{app_id}': row.app_id || '',
                            '{platform_id}': platform,
                          };
                          
                          let url = template;
                          Object.entries(replacements).forEach(([placeholder, value]) => {
                            url = url.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
                          });
                          
                          // If template has placeholders that weren't replaced, try building from platform data
                          if (url.includes('{') || url.includes('}')) {
                            return buildFallbackUrl();
                          }
                          
                          return url;
                        }
                        
                        // Dynamic fallback based on platform patterns
                        return buildFallbackUrl();
                      };
                      
                      const buildFallbackUrl = () => {
                        const platformLower = platform.toLowerCase();
                        
                        // Meta Quest URL building
                        if (platformLower.includes('meta') || platformLower.includes('oculus') || platformLower.includes('quest')) {
                          const slug = row.meta_slug || row.slug || row.experience_slug;
                          if (slug && (row.item_id || row.id)) return `https://www.meta.com/experiences/${slug}/${row.item_id || row.id}`;
                          if (row.item_id) return `https://www.meta.com/experiences/${row.item_id}`;
                          return `https://www.meta.com/search/?q=${encodeURIComponent(row.name || '')}`;
                        }
                        
                        // Steam URL building
                        if (platformLower.includes('steam')) {
                          // item_id is prefixed with its platform, e.g. "steam_639460"
                          const id = String(row.steam_appid || row.steam_app_id || row.steamappid || row.app_id || row.item_id || '').replace(/^steam_/i, '');
                          if (/^\d+$/.test(id)) return `https://store.steampowered.com/app/${id}`;
                          return `https://store.steampowered.com/search/?term=${encodeURIComponent(row.name || '')}`;
                        }

                        // Apple Vision URL building
                        if (platformLower.includes('apple')) {
                          // item_id is prefixed with its platform, e.g. "apple_vision_6478746501"
                          const id = String(row.apple_id || row.apple_item_id || row.appleid || row.app_id || row.item_id || '').replace(/^apple_vision_/i, '');
                          if (/^\d+$/.test(id)) return `https://apps.apple.com/app/id${id}`;
                          return `https://apps.apple.com/us/search?term=${encodeURIComponent(row.name || '')}`;
                        }

                        // Final fallback to Google search
                        return `https://www.google.com/search?q=${encodeURIComponent((row.name || '') + ' ' + platform)}`;
                      };

                      const openPlatform = (e) => {
                        e && e.stopPropagation && e.stopPropagation();
                        e && e.preventDefault && e.preventDefault();
                        const url = buildPlatformUrl();
                        try {
                          const a = document.createElement('a');
                          a.href = url;
                          a.target = '_blank';
                          a.rel = 'noopener noreferrer';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                        } catch (err) {
                          // fallback
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      };

                      return (
                        <button
                          type="button"
                          className="platform-link"
                          onClick={openPlatform}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPlatform(e); } }}
                          title={`Open on ${row.platform ? getPlatformDisplayName(row.platform) : 'store'}`}
                          aria-label={`Open ${row.name || 'app'} on ${row.platform ? getPlatformDisplayName(row.platform) : 'store'}`}
                        >
                          <PlatformIcon platform={platform} size={16} showLabel={false} />
                        </button>
                      );
                    })()}
                  </td>
              <td>
                <div className="rating">
                  <span>{row.rating?.average ? row.rating.average.toFixed(1) : 'N/A'}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 1L10.163 5.279L15 6.028L11.5 9.389L12.326 14L8 11.8L3.674 14L4.5 9.389L1 6.028L5.837 5.279L8 1Z" fill="#FFB800" />
                  </svg>
                </div>
              </td>
              <td>{(() => { const s = resolveAppScore(row); return s !== '' ? s : 'N/A'; })()}</td>
              <td>{row.downloads ? row.downloads.toLocaleString() : 'N/A'}</td>
              <td>{row.revenue ? `$${row.revenue.toLocaleString()}` : 'N/A'}</td>
              <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Export modal is triggered from the tabs area; render modal based on context state */}
      {showExportModal && (
        <div className="export-modal-overlay">
          <div className="export-modal">
            <div className="export-modal-header">
              <h4>Export</h4>
              <button
                className="export-close"
                aria-label="Close export modal"
                onClick={() => setShowExportModal(false)}
              >
                ×
              </button>
            </div>
            <div className="export-modal-body">
              <p>Select a format to download:</p>
              <div className="export-options">
                <button className="csv-primary" disabled={loader} title={loader ? 'Loading data...' : 'Download CSV'} onClick={() => { handleExport('csv'); setShowExportModal(false); }}>Download CSV</button>
                <button className="csv-secondary" disabled={loader} title={loader ? 'Loading data...' : 'Download XLSX'} onClick={() => { handleExport('xlsx'); setShowExportModal(false); }}>Download XLSX</button>
                <button className="csv-secondary" disabled={loader} title={loader ? 'Loading data...' : 'Download PDF'} onClick={() => { handleExport('pdf'); setShowExportModal(false); }}>Download PDF</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AppsTrackingPlanModal show={showUpgradeModal} handleClose={() => setShowUpgradeModal(false)} />
      <ExportUpgradeModal
        show={showExportInfoModal}
        handleClose={() => setShowExportInfoModal(false)}
        onUpgradeClick={() => {
          setShowExportInfoModal(false);
          setShowUpgradeModal(true);
        }}
      />
    </div>
  );
};

export default DataTable;
